import apiService from './apiService';
import { ErrorTypes, isErrorOfType } from '../utils/errorHandler';

export interface AdminPanelServerRow {
  id?: string | number;
  row_id?: string | number;
  parameter?: string;
  parameter_name?: string;
  machine_code?: string;
  line_code?: string;
  sku?: string;
  ideal_value?: number | null;
  threshhold?: number | null;
  units?: string | null;
  values?: Record<string, string | number | null | undefined>;
  sku_values?: Record<string, string | number | null | undefined>;
}

export interface MachineOption {
  key: string;
  label: string;
  code: string;
  lineCode: string;
}

export interface SaveAdminPanelResult {
  rows: AdminPanelServerRow[];
  updatedCount: number;
  status?: string;
  message?: string;
}

export interface SaveAdminPanelOptions {
  useEmptyStringForNullClear?: boolean;
}

let isAdminPanelEndpointUnavailable = false;

const normalizeRowsFromResponse = (response: any): AdminPanelServerRow[] => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.rows)) {
    return response.rows;
  }

  if (Array.isArray(response?.data?.rows)) {
    return response.data.rows;
  }

  // Supports backend shape: { status: 'success', data: [...] }
  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.configurations)) {
    return response.configurations;
  }

  return [];
};

/**
 * Get machine configuration for Admin Panel.
 *
 * To enable backend integration, set:
 * VITE_ADMIN_PANEL_CONFIG_ENDPOINT=/your/get/endpoint
 */
export const getAdminPanelMachineConfiguration = async (
  _machineKey: string,
  machineCode: string,
  lineCode: string
): Promise<AdminPanelServerRow[]> => {
  const env = (import.meta as ImportMeta & { env?: Record<string, string> }).env;
  const endpoint = env?.VITE_ADMIN_PANEL_CONFIG_ENDPOINT || '/admin/golden-settings';

  if (isAdminPanelEndpointUnavailable) {
    return [];
  }

  // apiService baseURL already includes /api/v1. Strip it if provided in env.
  const normalizedEndpoint = endpoint.replace(/^\/api\/v1/i, '') || '/admin/golden-settings';

  // Backend GET API expects machine_code and line_code as query parameters
  // Backend schema: GET /api/v1/admin/golden-settings?machine_code=xxx&line_code=yyy
  const requestConfig = {
    params: {
      machine_code: machineCode,
      line_code: lineCode,
      _ts: Date.now(),
    },
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    },
    timeout: 60000,
  };

  let response: any;
  try {
    response = await apiService.get(normalizedEndpoint, requestConfig);
  } catch (error) {
    const status = (error as any)?.details?.status;
    if (status === 404) {
      isAdminPanelEndpointUnavailable = true;
      return [];
    }

    const apiBaseUrl = env?.VITE_API_BASE_URL || '';
    const canRetryWithLoopback =
      isErrorOfType(error, ErrorTypes.NETWORK_ERROR)
      && apiBaseUrl.includes('localhost');

    if (!canRetryWithLoopback) {
      throw error;
    }

    const loopbackBaseUrl = `${apiBaseUrl.replace('localhost', '127.0.0.1')}/api/v1`;

    response = await apiService.get(normalizedEndpoint, {
      ...requestConfig,
      baseURL: loopbackBaseUrl,
    });
  }

  const normalized = normalizeRowsFromResponse(response);
  return normalized;
};

export const isAdminPanelConfigApiEnabled = (): boolean => {
  const env = (import.meta as ImportMeta & { env?: Record<string, string> }).env;

  if (env?.MODE === 'test') {
    return false;
  }

  const endpoint = env?.VITE_ADMIN_PANEL_CONFIG_ENDPOINT || '/admin/golden-settings';
  const enabled = Boolean(endpoint) && !isAdminPanelEndpointUnavailable;
  return enabled;
};

const normalizeMachineLineCode = (value: unknown): string => {
  const normalized = String(value || '').trim().toLowerCase().replace(/[-\s]+/g, '_');

  if (normalized === '1' || normalized === '01' || normalized === 'line_01' || normalized === 'line1' || normalized === 'line_1') {
    return 'Line_1';
  }

  if (normalized === '2' || normalized === '02' || normalized === 'line_02' || normalized === 'line2' || normalized === 'line_2') {
    return 'Line_2';
  }

  return 'Line_1';
};

const inferMachineLineCodeFromMachineCode = (machineCode: string): string => {
  const code = String(machineCode || '').toLowerCase().trim();
  if (code.endsWith('_02')) {
    return 'Line_2';
  }

  return 'Line_1';
};

const normalizeMachineOption = (machine: any, fallbackLineId?: string): MachineOption => {
  const rawCode = String(machine?.code || machine?.machine_code || machine?.machine_id || '').trim();
  const code = rawCode || 'unknown_machine';
  const rawLineCode = machine?.lineCode ?? machine?.line_code ?? machine?.line_id ?? fallbackLineId;
  const lineCode = rawLineCode
    ? normalizeMachineLineCode(rawLineCode)
    : inferMachineLineCodeFromMachineCode(code);

  const fallbackLabel = code
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter: string) => letter.toUpperCase());

  const label = code.toLowerCase() === 'sleeve_applicator_02'
    ? 'Bottle Specification'
    : fallbackLabel;

  const key = String(machine?.key || `${code}-${lineCode.toLowerCase()}`).trim();

  return {
    key,
    label,
    code,
    lineCode,
  };
};

export const getAvailableMachinesByLine = async (lineId: string): Promise<MachineOption[]> => {
  const env = (import.meta as ImportMeta & { env?: Record<string, string> }).env;
  const machinesEndpoint = (env?.VITE_ADMIN_PANEL_MACHINES_ENDPOINT || '/machines').replace(/^\/api\/v1/i, '') || '/machines';
  const normalizedLineId = String(lineId || '').trim() || '1';

  try {
    const response = await apiService.get(machinesEndpoint, {
      params: { line_id: normalizedLineId },
      timeout: 60000,
    });

    if (Array.isArray(response?.machines)) {
      return response.machines.map((machine: any) => normalizeMachineOption(machine, normalizedLineId));
    }
    if (Array.isArray(response?.data?.machines)) {
      return response.data.machines.map((machine: any) => normalizeMachineOption(machine, normalizedLineId));
    }
    if (Array.isArray(response?.data)) {
      return response.data.map((machine: any) => normalizeMachineOption(machine, normalizedLineId));
    }
    if (Array.isArray(response)) {
      return response.map((machine: any) => normalizeMachineOption(machine, normalizedLineId));
    }

    return [];
  } catch (error) {
    throw error;
  }
};

/**
 * Get list of available machines for Admin Panel tabs.
 * Fetches from backend endpoint /admin/machines
 * Uses backend-only machine definitions.
 */
export const getAvailableMachines = async (): Promise<MachineOption[]> => {
  const env = (import.meta as ImportMeta & { env?: Record<string, string> }).env;
  const configuredLineIds = String(env?.VITE_ADMIN_PANEL_MACHINE_LINE_IDS || '1,2')
    .split(',')
    .map((lineId) => lineId.trim())
    .filter(Boolean);

  const lineIds = configuredLineIds.length > 0 ? Array.from(new Set(configuredLineIds)) : ['1', '2'];

  try {
    const responses = await Promise.all(lineIds.map((lineId) => getAvailableMachinesByLine(lineId)));
    const machines: MachineOption[] = responses.flat();

    const uniqueMachines = Array.from(new Map(machines.map((machine) => [machine.key, machine])).values());

    if (uniqueMachines.length > 0) {
      return uniqueMachines;
    }

    return [];
  } catch (error) {
    throw error;
  }
};

export default {
  getAdminPanelMachineConfiguration,
  isAdminPanelConfigApiEnabled,
  getAvailableMachines,
};

/**
 * Save updated admin panel configuration to backend.
 * 
 * Payload structure:
 * - updates: Only the changed rows with minimal fields (id, ideal_value, threshhold)
 * - snapshot_records: ALL rows for the touched SKUs with new values already applied
 * 
 * @param machineCode - The database machine code
 * @param updatedRows - Array of rows with updated values (only changed rows)
 * @param updatedBy - User identifier required by backend (email/username)
 * @param options - Additional options for save behavior
 * @param lineCode - Line code required by backend (e.g., "Line_1", "Line_2")
 * @param snapshotRecords - Full snapshot of all rows for touched SKUs with new values applied
 * @returns Save result including updated count from backend
 */
export const saveAdminPanelConfiguration = async (
  machineCode: string,
  updatedRows: AdminPanelServerRow[],
  updatedBy?: string,
  options?: SaveAdminPanelOptions,
  lineCode?: string,
  snapshotRecords?: AdminPanelServerRow[]
): Promise<SaveAdminPanelResult> => {
  const env = (import.meta as ImportMeta & { env?: Record<string, string> }).env;
  const endpoint = env?.VITE_ADMIN_PANEL_CONFIG_ENDPOINT || '/admin/golden-settings';
  const explicitSaveEndpoint = env?.VITE_ADMIN_PANEL_SAVE_ENDPOINT;
  const defaultUpdatedBy = env?.VITE_ADMIN_PANEL_UPDATED_BY || 'frontend-ui';

  // Endpoint unavailable in current session
  if (isAdminPanelEndpointUnavailable) {
    return {
      rows: updatedRows,
      updatedCount: updatedRows.length,
      status: 'skipped',
      message: 'Admin Panel API endpoint is unavailable in this environment.',
    };
  }

  const normalizedEndpoint = endpoint.replace(/^\/api\/v1/i, '') || '/admin/golden-settings';
  const normalizedSaveEndpoint = explicitSaveEndpoint
    ? explicitSaveEndpoint.replace(/^\/api\/v1/i, '')
    : normalizedEndpoint;
  const saveEndpoint = normalizedSaveEndpoint;

  try {
    const useEmptyStringForNullClear = options?.useEmptyStringForNullClear === true;
    
    // Transform changed rows to minimal update items: id, ideal_value, threshhold only.
    const cleanUpdates = updatedRows.map((row) => {
      const updateItem: Record<string, any> = {};

      const rowId = row.id || row.row_id;
      if (rowId !== undefined && rowId !== null && String(rowId).trim() !== '') {
        updateItem.id = rowId;
      }

      // Send ideal_value (null means clear).
      updateItem.ideal_value =
        row.ideal_value === undefined || row.ideal_value === null
          ? (useEmptyStringForNullClear ? '' : null)
          : row.ideal_value;

      // Send threshhold as a number (no ± prefix).
      updateItem.threshhold =
        row.threshhold === undefined || row.threshhold === null
          ? (useEmptyStringForNullClear ? '' : null)
          : row.threshhold;
      
      return updateItem;
    });

    // Build snapshot_records — always include all 8 fields per record.
    const cleanSnapshotRecords = (snapshotRecords || []).map((row) => {
      return {
        id: row.id ?? row.row_id ?? null,
        line_code: row.line_code ?? null,
        machine_code: row.machine_code ?? null,
        sku: row.sku ?? null,
        parameter_name: row.parameter_name ?? null,
        ideal_value: (row.ideal_value !== undefined && row.ideal_value !== null)
          ? row.ideal_value
          : null,
        threshhold: (row.threshhold !== undefined && row.threshhold !== null)
          ? row.threshhold
          : null,
        units: row.units ?? null,
      };
    });

    // Determine line_code: prefer explicit parameter, then infer from rows or machine code.
    const resolvedLineCode = lineCode
      || updatedRows.find((r) => r.line_code)?.line_code
      || inferMachineLineCodeFromMachineCode(machineCode);

    const payload: Record<string, any> = {
      line_code: resolvedLineCode,
      machine_code: machineCode,
      updated_by: (updatedBy || defaultUpdatedBy).trim(),
      updates: cleanUpdates,
    };

    // Include snapshot_records only when available (full SKU snapshot with new values applied)
    if (cleanSnapshotRecords.length > 0) {
      payload.snapshot_records = cleanSnapshotRecords;
    }

    const response = await apiService.post(saveEndpoint, payload);

    const normalized = normalizeRowsFromResponse(response);
    const updatedCount = Number(
      response?.updated_count
      ?? response?.data?.updated_count
      ?? response?.count
      ?? normalized.length
      ?? 0
    );
    return {
      rows: normalized,
      updatedCount,
      status: response?.status ?? response?.data?.status,
      message: response?.message ?? response?.data?.message,
    };
  } catch (error) {
    const status = (error as any)?.details?.status;
    if (status === 404) {
      isAdminPanelEndpointUnavailable = true;
    }
    throw error;
  }
};
